/**
 * Capa 5: Vista de Bienvenida del Onboarding — Paso 4
 * Fondo Verde Orgánico (#70a32c), Logo AviFácil Centrado,
 * Mensaje de Bienvenida Óptimo y Botón de Inicio con Animación Framer Motion.
 */

'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import confetti from 'canvas-confetti';
import { RegaloModal } from './RegaloModal';
import styles from './Step4BienvenidaView.module.css';

interface Step4BienvenidaViewProps {
  nombreUsuario?: string;
  nombreGranja?: string;
  onComenzar: () => void;
}

/* ─── Variantes de Entrada Escénica Coreografiada ─── */
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.16,
      delayChildren: 0.08,
    },
  },
};

const badgeVariants: Variants = {
  hidden: { scale: 0, rotate: -20, opacity: 0 },
  visible: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 18,
    },
  },
};

const titleVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const subtitleVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const buttonVariants: Variants = {
  hidden: { opacity: 0, scale: 0.88, y: 14 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 250,
      damping: 18,
    },
  },
};

export const Step4BienvenidaView: React.FC<Step4BienvenidaViewProps> = ({
  nombreUsuario,
  nombreGranja,
  onComenzar,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Celebración festiva con confeti al sincronizarse la animación
  useEffect(() => {
    try {
      const timer1 = setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 85,
          origin: { y: 0.55 },
          colors: ['#FFD700', '#EE5847', '#FFFFFF', '#70a32c', '#FFA000'],
        });
      }, 200);

      const timer2 = setTimeout(() => {
        confetti({
          particleCount: 40,
          angle: 60,
          spread: 55,
          origin: { x: 0.15, y: 0.6 },
          colors: ['#FFD700', '#EE5847', '#FFFFFF'],
        });
        confetti({
          particleCount: 40,
          angle: 120,
          spread: 55,
          origin: { x: 0.85, y: 0.6 },
          colors: ['#FFD700', '#EE5847', '#FFFFFF'],
        });
      }, 420);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } catch {
      // Degrada silenciosamente
    }
  }, []);

  return (
    <div className={styles.bienvenidaContainer}>
      <motion.div
        className={styles.contentWrapper}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* 1. Emblema / Logo AviFácil */}
        <motion.div variants={badgeVariants} className={styles.logoBadge}>
          <Image
            src="/Sign-up/avifacil-logo-badge.png"
            alt="AviFácil"
            width={52}
            height={52}
            priority
            className="object-contain drop-shadow-sm pointer-events-none"
          />
        </motion.div>

        {/* 2. Título Principal de Bienvenida */}
        <motion.h1 variants={titleVariants} className={styles.title}>
          <span className={styles.exclamationStart}>¡</span>
          Te damos la bienvenida a la familia{' '}
          <span className={styles.brandHighlight}>AviFácil</span>
          <span className={styles.exclamationEnd}>!</span>
        </motion.h1>

        {/* 3. Mensaje Subtítulo Óptimo */}
        <motion.p variants={subtitleVariants} className={styles.subtitle}>
          {nombreGranja
            ? `Tu granja "${nombreGranja}" está lista. Empieza hoy a transformar tu producción avícola.`
            : 'Tu cuenta y granja han sido configuradas con éxito. Estamos listos para comenzar.'}
        </motion.p>

        {/* 4. Botón CTA para abrir regalo */}
        <motion.div variants={buttonVariants}>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={styles.ctaButton}
          >
            <span className="material-symbols-outlined text-[20px]">
              redeem
            </span>
            <span>Abrir regalo</span>
          </button>
        </motion.div>
      </motion.div>

      {/* Modal del Regalo: 15 Días Plan Pro */}
      <RegaloModal
        isOpen={isModalOpen}
        nombreGranja={nombreGranja}
        onClose={() => setIsModalOpen(false)}
        onAceptar={onComenzar}
      />
    </div>
  );
};
